import { getQuizPage } from 'ducks/router'
import { mergeDeepRight, setPath, updatePath } from 'utils/object'

// actions
export const FETCH_RESPONDENT = 'respondent/FETCH'
export const fetchRespondent = () => ({ type: FETCH_RESPONDENT })

export const RESET_RESPONDENT = 'respondent/RESET'
export const resetRespondent = () => ({ type: RESET_RESPONDENT })

export const UPDATE_RESPONDENT = 'respondent/UPDATE'
export const updateRespondent = payload => ({
  type: UPDATE_RESPONDENT,
  payload,
})

export const START_PERSONALITY = 'personality/START'
export const startPersonality = () => ({ type: START_PERSONALITY })

export const FINISH_PERSONALITY = 'personality/FINISH'
export const finishPersonality = () => ({ type: FINISH_PERSONALITY })

export const CHANGE_PERSONALITY_ANSWER = 'personality/CHANGE_ANSWER'
export const changePersonalityAnswer = ({ instrument, key, value }) => ({
  type: CHANGE_PERSONALITY_ANSWER,
  payload: { instrument, key, value },
})

export const START_INTEREST = 'interest/START'
export const startInterest = () => ({ type: START_INTEREST })

export const FINISH_INTEREST = 'interest/FINISH'
export const finishInterest = () => ({ type: FINISH_INTEREST })

export const CHANGE_INTEREST_ANSWER = 'interest/CHANGE_ANSWER'
export const changeInterestAnswer = ({
  instrument,
  key,
  value,
}: {
  instrument?: string
  key?: string
  value: unknown
}) => ({
  type: CHANGE_INTEREST_ANSWER,
  payload: { instrument, key, value },
})

export const START_GMA = 'gma/START'
export const startGMA = ({ reason }) => ({
  type: START_GMA,
  payload: { reason },
})

export const FINISH_GMA = 'gma/FINISH'
export const finishGMA = ({ reason }) => ({
  type: FINISH_GMA,
  payload: { reason },
})

export const SUBMIT_GMA = 'respondent/SUBMIT_GMA'
export const submitGMA = () => ({ type: SUBMIT_GMA })

export const SUBMIT_PERSONALITY = 'respondent/SUBMIT_PERSONALITY'
export const submitPersonality = () => ({ type: SUBMIT_PERSONALITY })

export const SUBMIT_INTEREST = 'respondent/SUBMIT_INTEREST'
export const submitInterest = () => ({ type: SUBMIT_INTEREST })

export const CHANGE_GMA_ANSWER = 'gma/CHANGE_ANSWER'
export const changeGMAAnswer = ({ key, value }) => ({
  type: CHANGE_GMA_ANSWER,
  payload: { key, value },
})

export const CHANGE_LANGUAGE = 'respondent/CHANGE_LANGUAGE'
export const changeLanguage = language => ({
  type: CHANGE_LANGUAGE,
  payload: { language },
})

export const DEADLINE_PASSED = 'respondent/DEADLINE_PASSED'
export const deadlinePassed = () => ({ type: DEADLINE_PASSED })

export const REUSE_CONSENT = 'assessment/REUSE_CONSENT'
export const reuseConsent = (consent, reuseTests) => ({
  type: REUSE_CONSENT,
  payload: { consent, ...(reuseTests ? { reuse_tests: reuseTests } : {}) },
})

// reducer
const initialState = {
  uuid: null,
  name: null,
  email: null,
  status: null,
  loading: true,
  has_previous_results: false,
  reuse_selection: null,
  language: 'nb',
  deadline: null,
  deadline_display: null,
  deadline_tz: null,
  past_deadline: false,
  access_blocked: false,
  personality: {
    status: 'fetching',
    answers: {},
  },
  gma: {
    status: 'fetching',
    answers: {},
  },
  interest: {
    status: 'fetching',
    answers: {},
  },
}

const toggleSubmit = status =>
  status === 'confirmsubmit' ? 'started' : 'confirmsubmit'

const countSet = values => values.filter(value => value != null).length

const countGMA = state =>
  setPath(
    state,
    ['stats', 'gma', 'totalAnswers'],
    countSet(Object.values(state.gma.answers)),
  )

// answers are grouped by instrument: {instrument: {key: value}}
const countPersonality = state =>
  setPath(
    state,
    ['stats', 'personality', 'totalAnswers'],
    countSet(
      Object.values(state.personality.answers).flatMap(answers =>
        Object.values(answers ?? {}),
      ),
    ),
  )

const countInterest = state =>
  setPath(
    state,
    ['stats', 'interest', 'totalAnswers'],
    countSet(Object.values(state.interest.answers)),
  )

export const reducer = (state = initialState, action) => {
  switch (action.type) {
    case CHANGE_LANGUAGE:
      return { ...state, language: action.payload.language }
    case DEADLINE_PASSED:
      return { ...state, past_deadline: true, access_blocked: true }
    case RESET_RESPONDENT:
      return mergeDeepRight(state, initialState)
    case UPDATE_RESPONDENT:
      return { ...mergeDeepRight(state, action.payload), loading: false }
    case CHANGE_PERSONALITY_ANSWER: {
      const { instrument, key, value } = action.payload
      return countPersonality(
        setPath(state, ['personality', 'answers', instrument, key], value),
      )
    }
    case CHANGE_INTEREST_ANSWER: {
      const { key, value } = action.payload
      return countInterest(setPath(state, ['interest', 'answers', key], value))
    }
    case CHANGE_GMA_ANSWER: {
      const { key, value } = action.payload
      return countGMA(setPath(state, ['gma', 'answers', key], value))
    }
    case SUBMIT_PERSONALITY:
      return updatePath(state, ['personality', 'status'], toggleSubmit)
    case SUBMIT_GMA:
      return updatePath(state, ['gma', 'status'], toggleSubmit)
    case SUBMIT_INTEREST:
      return updatePath(state, ['interest', 'status'], toggleSubmit)
    default:
      return state
  }
}

// helper functions

// selectors
export const getRespondent = state => state?.respondent
export const getLanguage = state => state?.respondent?.language ?? 'en'

export const isAccessBlocked = state =>
  state?.respondent?.access_blocked ?? false

export const getGMA = state => ({
  ...state?.respondent?.stats?.gma,
  ...state?.respondent?.gma,
})
export const getPersonality = state => ({
  ...state?.respondent?.stats?.personality,
  ...state?.respondent?.personality,
})
export const getInterest = state => ({
  ...state?.respondent?.stats?.interest,
  ...state?.respondent?.interest,
})

type QuizPageProps = { page?: any; uuid?: string } | null

// one object, so an empty question selects the same scale every time
const noScale = {}

// The question a quiz page shows. An empty one has no `value` yet.
type QuizQuestion = {
  uuid?: string
  instrument?: string
  question?: string
  prompt?: any
  label: string
  scale: any
  value?: any
  current?: number
}

export const getGMAQuestion = (props?: QuizPageProps) => state => {
  const { page = 0, uuid } = props || getQuizPage(state)
  const { answers }: { answers: Record<string, string | null> } =
    state?.respondent?.gma
  const [question, value] = Object.entries(answers)[page]
  return { question, value, current: page, uuid }
}

export const getPersonalityQuestion =
  (props?: QuizPageProps) =>
  (state): QuizQuestion => {
    const { page = 0, uuid } = props || getQuizPage(state)
    const { instruments, questions, answers } = state?.respondent?.personality

    if (!instruments) return { label: '', scale: noScale }

    // `at` reads a negative page from the end, as the old lookup did
    const { instrument, key } = questions.at(page)
    const { scale, prompt, questions: qx } = instruments[instrument]
    const { label } = qx.find(question => question?.key === key)
    const value = answers?.[instrument]?.[key]

    const question = {
      uuid,
      instrument,
      question: key,
      prompt,
      label,
      scale,
      value,
      current: page,
    }

    return question
  }

const emptyInterestQuestion = (uuid, page = 0) => ({
  label: '',
  scale: noScale,
  uuid,
  current: page,
})

export const getInterestQuestion =
  (props?: QuizPageProps) =>
  (state): QuizQuestion => {
    const quizPage = props || getQuizPage(state)
    const page = Number.isFinite(quizPage.page) ? quizPage.page : 0
    const { uuid } = quizPage
    const { instruments, answers } = state?.respondent?.interest

    if (!instruments?.questions?.length) {
      return emptyInterestQuestion(uuid, page)
    }

    const questions = instruments.questions
    const questionAtPage = questions.at(page)

    if (!questionAtPage) {
      return emptyInterestQuestion(uuid, page)
    }

    const { key, text } = questionAtPage
    const { scale, prompt } = instruments
    const value = answers?.[key]

    return {
      uuid,
      question: key,
      prompt,
      label: text,
      scale,
      value,
      current: page,
    }
  }
